import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { buildVisitorConsentPdfViewerIframeSrc } from "./visitor-consent-attachment-preview.util";
import { useVisitorConsentPdfPreviewUrl } from "./use-visitor-consent-pdf-preview-url.hook";

type P = {
  fileViewerUrl: string;
  fileName: string;
  openLinkLabel: string;
  pdfPreviewUnavailableMessage: string;
  pdfPreviewLoadingMessage: string;
  pdfPreviewHeight?: number | string;
  linkButtonSx?: SxProps<Theme>;
  testIds?: {
    root?: string;
    pdf?: string;
    pdfEmbed?: string;
    pdfObject?: string;
    unavailable?: string;
    link?: string;
    loading?: string;
  };
};

const pdfViewerFrameStyle = { width: "100%", height: "100%", border: 0 } as const;

export function VisitorConsentPdfAttachmentPreview({
  fileViewerUrl,
  fileName,
  openLinkLabel,
  pdfPreviewUnavailableMessage,
  pdfPreviewLoadingMessage,
  pdfPreviewHeight = 320,
  linkButtonSx,
  testIds
}: Readonly<P>) {
  const { previewState, previewUrl } = useVisitorConsentPdfPreviewUrl(fileViewerUrl);
  const rootTestId = testIds?.root ?? "visitor-consent-attachment-preview";

  if (previewState === "unavailable") {
    return (
      <Stack
        spacing={1.5}
        data-testid={testIds?.unavailable ?? "visitor-consent-attachment-preview-unavailable"}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          bgcolor: "grey.50"
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {pdfPreviewUnavailableMessage}
        </Typography>
        <Button
          component="a"
          href={fileViewerUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
          sx={{ alignSelf: "flex-start", ...linkButtonSx }}
          data-testid={testIds?.link ?? "visitor-consent-view-attachment"}
        >
          {openLinkLabel}
        </Button>
      </Stack>
    );
  }

  const pdfPaneHeight =
    typeof pdfPreviewHeight === "number" ? `${pdfPreviewHeight}px` : pdfPreviewHeight;
  const pdfViewerUrl =
    previewState === "ready" ? buildVisitorConsentPdfViewerIframeSrc(previewUrl) : null;

  return (
    <Box
      data-testid={rootTestId}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        width: "100%",
        overflow: previewState === "ready" ? "auto" : "hidden",
        bgcolor: "grey.50",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(previewState === "ready"
          ? { minHeight: pdfPreviewHeight, maxHeight: "70vh" }
          : { height: pdfPreviewHeight })
      }}
    >
      {previewState === "loading" && (
        <Stack
          spacing={1}
          alignItems="center"
          data-testid={testIds?.loading ?? "visitor-consent-attachment-preview-loading"}
        >
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            {pdfPreviewLoadingMessage}
          </Typography>
        </Stack>
      )}
      {previewState === "ready" && pdfViewerUrl && (
        <Stack spacing={1} sx={{ width: "100%", py: 1 }}>
          <Box sx={{ width: "100%", height: pdfPaneHeight, overflow: "hidden" }}>
            <iframe
              title={fileName}
              src={pdfViewerUrl}
              data-testid={testIds?.pdf ?? "visitor-consent-attachment-preview-pdf"}
              style={pdfViewerFrameStyle}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
}
